import React, { useMemo, useState } from "react";
import "./App.css";
import logo from "./assets/logo.svg";
import phoneImg from "./assets/product-phone.svg";
import earbudsImg from "./assets/product-earbuds.svg";
import chargerImg from "./assets/product-charger.svg";

function StarRating({ value = 5 }) {
  const stars = "★★★★★☆☆☆☆☆".slice(5 - value, 10 - value);
  return (
    <div className="rating" aria-label={`${value} out of 5 stars`}>
      <span className="stars">{stars}</span>
      <span className="muted" style={{ marginLeft: 6 }}>{value}.0</span>
    </div>
  );
}

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function App() {
  const products = useMemo(() => ([
    {
      id: "pocketpro",
      name: "PocketPro Mini",
      price: "₹18,999",
      tag: "Best value phone",
      rating: 5,
      img: phoneImg,
      desc: "Compact build, 120Hz display, and a battery that lasts all day. Great for everyday use and light gaming.",
      highlights: ["120Hz display", "All‑day battery", "Smooth UI"]
    },
    {
      id: "airpulse",
      name: "AirPulse Buds",
      price: "₹2,499",
      tag: "Low latency",
      rating: 4,
      img: earbudsImg,
      desc: "Crisp vocals, punchy bass, and a low-latency mode for videos and casual gaming. Comfortable fit.",
      highlights: ["Fast pairing", "Bass boost", "Low latency"]
    },
    {
      id: "voltdock",
      name: "VoltDock 65W",
      price: "₹1,799",
      tag: "GaN fast charger",
      rating: 5,
      img: chargerImg,
      desc: "A compact 65W GaN charger that powers phones, tablets, and many laptops—without heating up.",
      highlights: ["65W output", "GaN efficiency", "Travel friendly"]
    },
  ]), []);

  const reviews = useMemo(() => ([
    {
      name: "Aarav S.",
      title: "Honest and practical recommendations",
      text: "I bought the VoltDock after reading the review—exactly as described. Fast charging and stays cool.",
      rating: 5
    },
    {
      name: "Meera K.",
      title: "Great picks for budget gadgets",
      text: "PocketPro Mini is a solid deal. The site helped me compare quickly and the highlights were accurate.",
      rating: 5
    },
    {
      name: "Rohan P.",
      title: "Clear pros/cons",
      text: "AirPulse Buds review matched my experience. Loved the latency notes—made it easy to decide.",
      rating: 4
    }
  ]), []);

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("");

  function onSubmit(e) {
    e.preventDefault();
    setStatus("Thanks! We received your message. (Demo form — connect it to your email/service.)");
    setForm({ name: "", email: "", message: "" });
    setTimeout(() => setStatus(""), 6000);
  }

  return (
    <div>
      {/* Header */}
      <header className="topbar">
        <div className="container nav">
          <a className="brand" href="#home" onClick={(e) => { e.preventDefault(); scrollToId("home"); }}>
            <img src={logo} alt="GadgetBiz logo" />
            <span>GADGET 4U</span>
          </a>

          <nav className="navLinks" aria-label="Primary">
            <a href="#products" onClick={(e) => { e.preventDefault(); scrollToId("products"); }}>Products</a>
            <a href="#reviews" onClick={(e) => { e.preventDefault(); scrollToId("reviews"); }}>Reviews</a>
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToId("contact"); }}>Contact</a>
            <button className="btn primary" onClick={() => scrollToId("contact")}>Get a quote</button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <main id="home" className="section">
        <div className="container split">
          <div>
            <span className="kicker"><span className="dot" /> Gadgets you’ll actually enjoy using</span>
            <h1 className="h1">Buy smarter gadgets, with clear reviews and real-world picks.</h1>
            <p className="muted" style={{ fontSize: 16, maxWidth: 60*10 }}>
              We sell a small set of gadgets we’ve tested: reliable phones, audio gear, and power accessories.
              Simple descriptions, honest pros/cons, and fast support.
            </p>
            <div className="row" style={{ marginTop: 16 }}>
              <button className="btn primary" onClick={() => scrollToId("products")}>Browse products</button>
              <button className="btn" onClick={() => scrollToId("reviews")}>See customer reviews</button>
            </div>

            <div className="grid" style={{ marginTop: 20, gridTemplateColumns: "repeat(3, minmax(0,1fr))" }}>
              <div className="heroStat">
                <div className="avatar" aria-hidden>✓</div>
                <div><strong>Curated</strong><span>Only best picks</span></div>
              </div>
              <div className="heroStat">
                <div className="avatar" aria-hidden>★</div>
                <div><strong>Rated</strong><span>Clear pros & cons</span></div>
              </div>
              <div className="heroStat">
                <div className="avatar" aria-hidden>⚡</div>
                <div><strong>Fast help</strong><span>Quick response</span></div>
              </div>
            </div>
          </div>

          <div className="card heroCard">
            <div className="heroArt">
              <svg className="device" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 420" role="img" aria-label="Gadget illustration">
                <defs>
                  <linearGradient id="hbg" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0" stopColor="rgba(124,58,237,.55)" />
                    <stop offset="1" stopColor="rgba(34,197,94,.35)" />
                  </linearGradient>
                  <linearGradient id="scr" x1="0" x2="1">
                    <stop offset="0" stopColor="rgba(14,165,233,.38)" />
                    <stop offset="1" stopColor="rgba(167,139,250,.38)" />
                  </linearGradient>
                </defs>
                <rect x="0" y="0" width="640" height="420" rx="26" fill="url(#hbg)" opacity=".35"/>
                <g transform="translate(78 70)">
                  <rect x="0" y="0" width="250" height="320" rx="28" fill="#0b1220" stroke="rgba(148,163,184,.45)" strokeWidth="3"/>
                  <rect x="18" y="38" width="214" height="230" rx="18" fill="url(#scr)" stroke="rgba(229,231,235,.14)" strokeWidth="2"/>
                  <rect x="92" y="18" width="66" height="10" rx="5" fill="rgba(229,231,235,.35)"/>
                  <circle cx="125" cy="287" r="12" fill="rgba(229,231,235,.18)"/>
                  <circle cx="125" cy="287" r="5" fill="rgba(229,231,235,.55)"/>
                </g>
                <g transform="translate(370 115)">
                  <rect x="0" y="0" width="190" height="260" rx="28" fill="#0b1220" stroke="rgba(148,163,184,.45)" strokeWidth="3"/>
                  <rect x="20" y="22" width="150" height="150" rx="24" fill="rgba(34,197,94,.15)" stroke="rgba(229,231,235,.12)" strokeWidth="2"/>
                  <path d="M98 54l-22 44h26l-10 44 30-56h-26l12-32z" fill="rgba(229,231,235,.86)"/>
                  <g transform="translate(45 186)" stroke="rgba(229,231,235,.40)" strokeWidth="6" strokeLinecap="round">
                    <line x1="0" y1="0" x2="0" y2="24"/>
                    <line x1="100" y1="0" x2="100" y2="24"/>
                  </g>
                </g>
                <text x="42" y="44" fill="rgba(229,231,235,.92)" fontFamily="system-ui, -apple-system, Segoe UI, Roboto" fontWeight="800" fontSize="22">
                  Weekly gadget drops • Honest reviews • Great pricing
                </text>
              </svg>
            </div>
          </div>
        </div>
      </main>

      {/* Products */}
      <section id="products" className="section">
        <div className="container">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <div>
              <span className="kicker"><span className="dot" /> Featured products</span>
              <h2 className="h2" style={{ marginTop: 10 }}>Handpicked gadgets we recommend</h2>
              <p className="muted" style={{ marginTop: 0 }}>A small lineup that covers the essentials: phone, audio, and power.</p>
            </div>
            <span className="pill">Updated weekly • Ships fast</span>
          </div>

          <div className="grid productsGrid" style={{ marginTop: 18 }}>
            {products.map((p) => (
              <article key={p.id} className="card">
                <div className="productImgWrap">
                  <img src={p.img} alt={p.name} />
                </div>
                <div className="cardPad">
                  <div className="row" style={{ justifyContent: "space-between" }}>
                    <h3 className="h3">{p.name}</h3>
                    <span className="pill">{p.price}</span>
                  </div>
                  <div className="row" style={{ justifyContent: "space-between", marginTop: 8 }}>
                    <span className="pill">{p.tag}</span>
                    <StarRating value={p.rating} />
                  </div>
                  <p className="muted" style={{ marginTop: 12 }}>{p.desc}</p>
                  <div className="row" style={{ marginTop: 10 }}>
                    {p.highlights.map((h) => <span className="pill" key={h}>{h}</span>)}
                  </div>
                  <div className="row" style={{ marginTop: 14 }}>
                    <button className="btn primary" onClick={() => scrollToId("contact")}>Enquire</button>
                    <button className="btn" onClick={() => alert(`Demo: Add ${p.name} to cart`)}>Add to cart</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="section">
        <div className="container">
          <span className="kicker"><span className="dot" /> Customer reviews</span>
          <h2 className="h2" style={{ marginTop: 10 }}>What people say</h2>
          <p className="muted" style={{ marginTop: 0, maxWidth: 720 }}>
            We keep reviews short and specific—so you can judge quickly if a gadget is right for you.
          </p>

          <div className="grid reviewsGrid" style={{ marginTop: 18 }}>
            {reviews.map((r, idx) => (
              <article key={idx} className="card">
                <div className="cardPad">
                  <div className="row" style={{ justifyContent: "space-between" }}>
                    <div className="row">
                      <div className="avatar" aria-hidden>{r.name.split(" ")[0][0]}{r.name.split(" ")[1]?.[0] || ""}</div>
                      <div>
                        <div style={{ fontWeight: 800 }}>{r.name}</div>
                        <div className="muted" style={{ fontSize: 13 }}>{r.title}</div>
                      </div>
                    </div>
                    <StarRating value={r.rating} />
                  </div>
                  <p style={{ marginTop: 12 }}>{r.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="section">
        <div className="container">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <div>
              <span className="kicker"><span className="dot" /> Contact us</span>
              <h2 className="h2" style={{ marginTop: 10 }}>Want a recommendation or bulk quote?</h2>
              <p className="muted" style={{ marginTop: 0 }}>Send us a message and we’ll reply with the best options.</p>
            </div>
            <span className="pill">Support: Mon–Sat, 10am–7pm</span>
          </div>

          <div className="grid contactWrap" style={{ marginTop: 18 }}>
            <form className="card" onSubmit={onSubmit}>
              <div className="cardPad">
                <div className="field">
                  <label htmlFor="name">Name</label>
                  <input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" required />
                </div>
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" required />
                </div>
                <div className="field">
                  <label htmlFor="msg">Message</label>
                  <textarea id="msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What gadget are you looking for?" required />
                </div>
                <div className="row">
                  <button className="btn primary" type="submit">Send message</button>
                  <button className="btn" type="button" onClick={() => setForm({ name: "", email: "", message: "" })}>Clear</button>
                </div>
                {status ? <p className="muted" style={{ marginTop: 12 }}>{status}</p> : null}
              </div>
            </form>

            <aside className="card">
              <div className="cardPad">
                <h3 className="h3">Business details</h3>
                <p className="muted" style={{ marginTop: 6 }}>
                  <strong style={{ color: "var(--text)" }}>Gadget 4U Store</strong><br/>
                  Name: <a>Jatin Singh</a><br/>
                  Email: <a href="mailto:admin@gadget4u.info">admin@gadget4u.info</a><br/>
                  Phone: <a href="tel:+919876543210">+91 8437253624</a><br/>
                  Address: Seelampur Sub Post Office, J-8, KHN 671, Gali Number 04, Jai
Prakash Nagar Ghonda, New Delhi, Shahdara, Delhi, 110053
                </p>

                <div className="card" style={{ marginTop: 14, boxShadow: "none" }}>
                  <div className="cardPad">
                    <div className="row" style={{ justifyContent: "space-between" }}>
                      <span className="pill">Quick tips</span>
                      <span className="pill">1–2 day dispatch</span>
                    </div>
                    <ul className="muted" style={{ marginTop: 12, paddingLeft: 18 }}>
                      <li>Tell us your budget and usage (work, gaming, travel).</li>
                      <li>We’ll suggest 2–3 best-fit gadgets with pros/cons.</li>
                      <li>Bulk orders available for teams and events.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container footerRow">
          <div className="row">
            <img src={logo} alt="GadgetBiz" style={{ width: 38, height: 38 }} />
            <div>
              <div style={{ fontWeight: 900 }}>Gadget 4U</div>
              <div className="muted" style={{ fontSize: 13 }}>Gadgets • Descriptions • Reviews</div>
            </div>
          </div>

          <div className="tinyLinks" aria-label="Footer links">
            <a href="#home" onClick={(e) => { e.preventDefault(); scrollToId("home"); }}>Home</a>
            <a href="#products" onClick={(e) => { e.preventDefault(); scrollToId("products"); }}>Products</a>
            <a href="#reviews" onClick={(e) => { e.preventDefault(); scrollToId("reviews"); }}>Reviews</a>
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToId("contact"); }}>Contact</a>
          </div>

          <div className="muted" style={{ fontSize: 13 }}>
            © {new Date().getFullYear()} Gadget 4U. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
